import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical, Play, CheckCircle2, XCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '@/api/client';
import { runPreview, runBatchPreview } from '@/api/simulator';
import { EntitlementGate } from '@/components/billing/EntitlementGate';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { ApiRequestError } from '@/types/api';
import type { RetrievalOutcome } from '@/types/retrieval';
import type { PreviewRequest, PreviewResponse } from '@/api/simulator';
import type { VectorDBConnector } from '@/types/connector';
import type { RetrievalDiagnostics } from '@/types/answer';

interface Principal {
  id: string;
  display_name: string;
  email: string;
  groups: string[];
  roles: string[];
  attributes: Record<string, unknown>;
}

interface SimulationResult {
  outcome: RetrievalOutcome;
  matched_resources: number;
  allowed: number;
  denied: number;
  policy_trace: { policy_id: string; policy_name: string; effect: string; evaluation_ms?: number; matched_rules?: string[] }[];
  denial_reasons: string[];
}

const outcomeConfig = {
  allowed: { icon: CheckCircle2, label: 'Allowed', className: 'text-status-allow', bgClassName: 'bg-status-allow/10' },
  partial: { icon: AlertTriangle, label: 'Partial', className: 'text-status-warning', bgClassName: 'bg-status-warning/10' },
  denied: { icon: XCircle, label: 'Denied', className: 'text-status-deny', bgClassName: 'bg-status-deny/10' },
};

const SEARCH_MODES = ['vector', 'keyword', 'hybrid'] as const;
type SearchMode = typeof SEARCH_MODES[number];

function SimulatorDiagnosticsPanel({ diagnostics }: { diagnostics: RetrievalDiagnostics }) {
  return (
    <details className="rounded-lg border bg-card group">
      <summary className="flex cursor-pointer items-center gap-2 px-5 py-3 text-sm font-medium select-none list-none [&::-webkit-details-marker]:hidden hover:bg-muted/40 rounded-lg transition-colors">
        <AlertTriangle className="h-4 w-4 text-status-warning shrink-0" />
        <span>Retrieval diagnostics</span>
        <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>

      <div className="px-5 pb-4 pt-1 space-y-3 border-t">
        {/* outcome_detail — lead explanation */}
        {diagnostics.outcome_detail && (
          <div className="rounded-md bg-status-warning/10 border border-status-warning/20 px-4 py-3">
            <p className="text-xs font-medium text-status-warning mb-0.5">Why these results</p>
            <p className="text-sm">{diagnostics.outcome_detail}</p>
          </div>
        )}

        {/* Counts */}
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="rounded-md border px-3 py-2 text-center">
            <p className="text-xs text-muted-foreground">Fetched</p>
            <p className="font-semibold tabular mt-0.5">{diagnostics.candidates_fetched}</p>
          </div>
          <div className="rounded-md border border-status-allow/30 bg-status-allow/5 px-3 py-2 text-center">
            <p className="text-xs text-muted-foreground">Allowed</p>
            <p className="font-semibold tabular text-status-allow mt-0.5">{diagnostics.candidates_allowed}</p>
          </div>
          <div className="rounded-md border border-status-deny/30 bg-status-deny/5 px-3 py-2 text-center">
            <p className="text-xs text-muted-foreground">Denied</p>
            <p className="font-semibold tabular text-status-deny mt-0.5">{diagnostics.candidates_denied}</p>
          </div>
        </div>

        {diagnostics.refill_rounds > 0 && (
          <p className="text-xs text-muted-foreground">
            Refill ran {diagnostics.refill_rounds} round{diagnostics.refill_rounds !== 1 ? 's' : ''} to find authorized content.
          </p>
        )}

        {diagnostics.policies_evaluated > 0 && (
          <p className="text-xs text-muted-foreground">
            {diagnostics.policies_evaluated} {diagnostics.policies_evaluated === 1 ? 'policy' : 'policies'} evaluated.
          </p>
        )}

        {diagnostics.active_denial_reasons.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Top denial reasons</p>
            {diagnostics.active_denial_reasons.slice(0, 3).map((reason, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-status-deny">
                <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{reason}</span>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground pt-1">
          Compare principals with{' '}
          <Link to="/access-simulator?tab=batch-preview" className="text-primary underline underline-offset-2">
            Batch Preview
          </Link>
          {' '}or review policies in{' '}
          <Link to="/policy-studio" className="text-primary underline underline-offset-2">
            Policy Studio
          </Link>
          .
        </p>
      </div>
    </details>
  );
}

export function AccessSimulatorPage() {
  const [activeTab, setActiveTab] = useState<'dry-run' | 'live-preview' | 'batch-preview'>('dry-run');

  // Dry Run state
  const [principalId, setPrincipalId] = useState('');
  const [query, setQuery] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [error, setError] = useState<string>();

  // Live Preview state
  const [previewPrincipalId, setPreviewPrincipalId] = useState('');
  const [previewConnectorId, setPreviewConnectorId] = useState('');
  const [previewQuery, setPreviewQuery] = useState('');
  const [previewSearchMode, setPreviewSearchMode] = useState<SearchMode>('vector');
  const [previewAlpha, setPreviewAlpha] = useState(0.5);
  const [previewTopK, setPreviewTopK] = useState(10);
  const [previewRunning, setPreviewRunning] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string>();

  // Batch Preview state
  const [batchPrincipalIds, setBatchPrincipalIds] = useState<string[]>([]);
  const [batchConnectorId, setBatchConnectorId] = useState('');
  const [batchQuery, setBatchQuery] = useState('');
  const [batchSearchMode, setBatchSearchMode] = useState<SearchMode>('vector');
  const [batchAlpha, setBatchAlpha] = useState(0.5);
  const [batchTopK] = useState(10);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResult, setBatchResult] = useState<PreviewResponse[] | null>(null);
  const [batchError, setBatchError] = useState<string>();

  const { data: principals, isLoading: principalsLoading } = useQuery({
    queryKey: ['principals-for-simulator'],
    queryFn: async () => {
      const res = await apiGet<{ data: Principal[] }>('/principals', { per_page: 100 });
      return res.data;
    },
  });

  const { data: connectors, isLoading: connectorsLoading } = useQuery({
    queryKey: ['connectors-for-simulator'],
    queryFn: async () => {
      const res = await apiGet<{ data: VectorDBConnector[] }>('/connectors');
      return res.data;
    },
    enabled: activeTab === 'live-preview' || activeTab === 'batch-preview',
  });

  async function handleSimulate(e: React.FormEvent) {
    e.preventDefault();
    if (!principalId || !query.trim()) return;
    setIsRunning(true);
    setResult(null);
    setError(undefined);
    try {
      const res = await apiPost<SimulationResult>('/simulator/run', {
        principal_id: principalId,
        query: query.trim(),
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Simulation failed');
    } finally {
      setIsRunning(false);
    }
  }

  async function handlePreview(e: React.FormEvent) {
    e.preventDefault();
    if (!previewPrincipalId || !previewConnectorId || !previewQuery.trim()) return;
    setPreviewRunning(true);
    setPreviewResult(null);
    setPreviewError(undefined);
    try {
      const req: PreviewRequest = {
        principal_id: previewPrincipalId,
        connector_id: previewConnectorId,
        query: previewQuery.trim(),
        top_k: previewTopK,
        search_mode: previewSearchMode,
      };
      if (previewSearchMode === 'hybrid') {
        req.alpha = previewAlpha;
      }
      const res = await runPreview(req);
      setPreviewResult(res);
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 429) {
        setPreviewError('Rate limit reached. Up to 10 live previews per minute per org. Please wait a moment and try again.');
      } else {
        setPreviewError(err instanceof Error ? err.message : 'Preview failed');
      }
    } finally {
      setPreviewRunning(false);
    }
  }

  async function handleBatchPreview(e: React.FormEvent) {
    e.preventDefault();
    if (batchPrincipalIds.length === 0 || !batchConnectorId || !batchQuery.trim()) return;
    setBatchRunning(true);
    setBatchResult(null);
    setBatchError(undefined);
    try {
      const req = {
        principal_ids: batchPrincipalIds,
        connector_id: batchConnectorId,
        query: batchQuery.trim(),
        top_k: batchTopK,
        search_mode: batchSearchMode,
        ...(batchSearchMode === 'hybrid' ? { alpha: batchAlpha } : {}),
      };
      const res = await runBatchPreview(req);
      setBatchResult(res.data);
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 429) {
        setBatchError('Rate limit reached. Please wait a moment and try again.');
      } else {
        setBatchError(err instanceof Error ? err.message : 'Batch preview failed');
      }
    } finally {
      setBatchRunning(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Govern"
        title="Access Simulator"
        subtitle="Simulate retrieval queries to see exactly what a principal can access before deploying policies to production."
      />

      <EntitlementGate feature="access_simulator">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'dry-run' | 'live-preview' | 'batch-preview')}>
          <TabsList>
            <TabsTrigger value="dry-run">Dry Run</TabsTrigger>
            <TabsTrigger value="live-preview">Live Preview</TabsTrigger>
            <TabsTrigger value="batch-preview">Batch (up to 5)</TabsTrigger>
          </TabsList>

          {/* Dry Run tab */}
          <TabsContent value="dry-run">
            <div className="space-y-4 mt-4">
              <div className="rounded-lg border bg-card p-6">
                <h3 className="font-semibold mb-4">Run Simulation</h3>
                <form onSubmit={handleSimulate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Principal</label>
                      {principalsLoading ? (
                        <Skeleton className="h-10 w-full" />
                      ) : (
                        <Select value={principalId} onValueChange={(v) => setPrincipalId(v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a principal..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(principals ?? []).map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.display_name} ({p.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Query</label>
                      <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="e.g. Show me the customer support transcripts"
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={!principalId || !query.trim() || isRunning}>
                    <Play className="h-4 w-4 mr-2" />
                    {isRunning ? 'Simulating...' : 'Run Simulation'}
                  </Button>
                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}
                </form>
              </div>

              {result && (
                <div className="space-y-4">
                  {/* Outcome Summary */}
                  <div className={`rounded-lg border p-6 ${(outcomeConfig[result.outcome] ?? outcomeConfig.denied).bgClassName}`}>
                    <div className="flex items-center gap-3 mb-4">
                      {(() => {
                        const cfg = outcomeConfig[result.outcome] ?? outcomeConfig.denied;
                        const Icon = cfg.icon;
                        return <Icon className={`h-6 w-6 ${cfg.className}`} />;
                      })()}
                      <div>
                        <h3 className={`font-semibold text-lg ${(outcomeConfig[result.outcome] ?? outcomeConfig.denied).className}`}>
                          {(outcomeConfig[result.outcome] ?? outcomeConfig.denied).label}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          &ldquo;{query}&rdquo;
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Matched Resources</p>
                        <p className="font-semibold">{result.matched_resources}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Allowed</p>
                        <p className="font-semibold tabular text-status-allow">{result.allowed}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Denied</p>
                        <p className="font-semibold tabular text-status-deny">{result.denied}</p>
                      </div>
                    </div>
                  </div>

                  {/* Policy Trace */}
                  {result.policy_trace.length > 0 && (
                    <div className="rounded-lg border bg-card p-6">
                      <h3 className="font-semibold mb-3">Policy Trace</h3>
                      <div className="space-y-2">
                        {result.policy_trace.map((trace, i) => (
                          <div key={i} className="flex items-center justify-between rounded-md border px-4 py-3 text-sm">
                            <div className="flex items-center gap-3">
                              {trace.effect === 'allow' ? (
                                <CheckCircle2 className="h-4 w-4 text-status-allow shrink-0" />
                              ) : (
                                <XCircle className="h-4 w-4 text-status-deny shrink-0" />
                              )}
                              <p className="font-medium">{trace.policy_name}</p>
                            </div>
                            <Badge variant={trace.effect === 'allow' ? 'default' : 'destructive'}>
                              {trace.effect}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Denial Reasons */}
                  {result.denial_reasons.length > 0 && (
                    <div className="rounded-lg border bg-card p-6">
                      <h3 className="font-semibold mb-3">Denial Reasons</h3>
                      <div className="space-y-2">
                        {result.denial_reasons.map((reason, i) => (
                          <div key={i} className="flex items-start gap-3 rounded-md border border-status-deny/30 bg-status-deny/10 px-4 py-3 text-sm">
                            <XCircle className="h-4 w-4 text-status-deny mt-0.5 shrink-0" />
                            <p className="text-status-deny text-xs">{reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!result && !isRunning && (
                <div className="rounded-lg border border-dashed p-12 text-center">
                  <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Select a principal and enter a query</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Choose a synced principal and describe what they&apos;re trying to access. The simulator will evaluate all active policies against the real data catalog.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Live Preview tab */}
          <TabsContent value="live-preview">
            <div className="space-y-4 mt-4">
              <div className="rounded-lg border bg-card p-6">
                <h3 className="font-semibold mb-4">Run Live Preview</h3>
                <form onSubmit={handlePreview} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Principal */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Principal</label>
                      {principalsLoading ? (
                        <Skeleton className="h-10 w-full" />
                      ) : (
                        <Select value={previewPrincipalId} onValueChange={(v) => setPreviewPrincipalId(v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a principal..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(principals ?? []).map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.display_name} ({p.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Connector */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Connector</label>
                      {connectorsLoading ? (
                        <Skeleton className="h-10 w-full" />
                      ) : (
                        <Select value={previewConnectorId} onValueChange={(v) => setPreviewConnectorId(v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a connector..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(connectors ?? []).map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} ({c.type})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Query */}
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium">Query</label>
                      <Input
                        value={previewQuery}
                        onChange={(e) => setPreviewQuery(e.target.value)}
                        placeholder="e.g. confidential financial reports"
                      />
                    </div>

                    {/* Search Mode */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Search Mode</label>
                      <Select value={previewSearchMode} onValueChange={(v) => setPreviewSearchMode(v as SearchMode)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select search mode..." />
                        </SelectTrigger>
                        <SelectContent>
                          {SEARCH_MODES.map((mode) => (
                            <SelectItem key={mode} value={mode}>
                              {mode}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Top-K */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Top-K Results</label>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        value={previewTopK}
                        onChange={(e) => setPreviewTopK(Math.min(20, Math.max(1, Number(e.target.value))))}
                      />
                    </div>
                  </div>

                  {/* Alpha slider — only visible for hybrid mode */}
                  {previewSearchMode === 'hybrid' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Alpha (vector vs keyword weight)</label>
                        <span className="text-sm text-muted-foreground font-mono">{previewAlpha.toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={previewAlpha}
                        onChange={(e) => setPreviewAlpha(Number(e.target.value))}
                        className="w-full accent-primary"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Keyword</span>
                        <span>Vector</span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={!previewPrincipalId || !previewConnectorId || !previewQuery.trim() || previewRunning}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {previewRunning ? 'Running Preview...' : 'Run Preview'}
                  </Button>

                  {previewError && (
                    <p className="text-sm text-destructive">{previewError}</p>
                  )}
                </form>
              </div>

              {/* Preview Results */}
              {previewResult && (
                <div className="space-y-4">
                  {/* Summary bar */}
                  <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-card px-5 py-4 text-sm">
                    {(() => {
                      const cfg = outcomeConfig[previewResult.outcome as keyof typeof outcomeConfig] ?? outcomeConfig.denied;
                      const Icon = cfg.icon;
                      return (
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${cfg.className}`} />
                          <span className={`font-semibold ${cfg.className}`}>{cfg.label}</span>
                        </div>
                      );
                    })()}
                    <span className="text-muted-foreground">{Math.round(previewResult.timing_ms)}ms</span>
                    <span className="text-muted-foreground">{previewResult.total_results} total results</span>
                    <Badge variant="outline">{previewResult.search_mode}</Badge>
                    <span className="text-muted-foreground truncate max-w-xs">&ldquo;{previewResult.query}&rdquo;</span>
                  </div>

                  {/* Diagnostics panel — shown when API returns diagnostics */}
                  {previewResult.diagnostics != null && (
                    <SimulatorDiagnosticsPanel diagnostics={previewResult.diagnostics} />
                  )}
                  {previewResult.diagnostics == null && (
                    <p className="text-xs text-muted-foreground px-1">
                      Detailed diagnostics available from API v1.1.
                    </p>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Allowed panel */}
                    <div className="rounded-lg border border-status-allow/30 bg-card">
                      <div className="flex items-center gap-2 px-4 py-3 border-b border-status-allow/30 bg-status-allow/5 rounded-t-lg">
                        <CheckCircle2 className="h-4 w-4 text-status-allow" />
                        <span className="text-sm font-semibold text-status-allow">
                          Allowed ({previewResult.allowed_results.length})
                        </span>
                      </div>
                      {previewResult.allowed_results.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-4">No allowed results.</p>
                      ) : (
                        <div className="divide-y">
                          {previewResult.allowed_results.map((item) => (
                            <div key={item.vector_id} className="p-4 space-y-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-sm font-medium truncate">
                                  {item.resource_title ?? item.vector_id}
                                </span>
                                <span className="text-xs font-mono bg-muted rounded px-1.5 py-0.5 shrink-0">
                                  {item.score.toFixed(3)}
                                </span>
                              </div>
                              {item.content_preview && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {item.content_preview}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-1">
                                {item.classification && (
                                  <span className="text-xs bg-primary/10 text-primary rounded px-1.5 py-0.5">
                                    {item.classification}
                                  </span>
                                )}
                                {item.domain && (
                                  <span className="text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5">
                                    {item.domain}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Denied panel */}
                    <div className="rounded-lg border border-status-deny/30 bg-card">
                      <div className="flex items-center gap-2 px-4 py-3 border-b border-status-deny/30 bg-status-deny/5 rounded-t-lg">
                        <XCircle className="h-4 w-4 text-status-deny" />
                        <span className="text-sm font-semibold text-status-deny">
                          Denied ({previewResult.denied_results.length})
                        </span>
                      </div>
                      {previewResult.denied_results.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-4">No denied results.</p>
                      ) : (
                        <div className="divide-y">
                          {previewResult.denied_results.map((item) => (
                            <div key={item.vector_id} className="p-4 space-y-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-sm font-medium truncate">
                                  {item.resource_title ?? item.vector_id}
                                </span>
                                <span className="text-xs font-mono bg-muted rounded px-1.5 py-0.5 shrink-0">
                                  {item.score.toFixed(3)}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {item.denial_reasons.map((reason, i) => (
                                  <p key={i} className="text-xs text-status-deny">{reason}</p>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {item.classification && (
                                  <span className="text-xs bg-primary/10 text-primary rounded px-1.5 py-0.5">
                                    {item.classification}
                                  </span>
                                )}
                                {item.domain && (
                                  <span className="text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5">
                                    {item.domain}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Policy Trace */}
                  {previewResult.policy_trace.length > 0 && (
                    <div className="rounded-lg border bg-card p-6">
                      <h3 className="font-semibold mb-3">Policy Trace</h3>
                      <div className="space-y-2">
                        {previewResult.policy_trace.map((trace, i) => (
                          <div key={i} className="flex items-center justify-between rounded-md border px-4 py-3 text-sm">
                            <div className="flex items-center gap-3">
                              {trace.effect === 'allow' ? (
                                <CheckCircle2 className="h-4 w-4 text-status-allow shrink-0" />
                              ) : (
                                <XCircle className="h-4 w-4 text-status-deny shrink-0" />
                              )}
                              <p className="font-medium">{trace.policy_name}</p>
                            </div>
                            <Badge variant={trace.effect === 'allow' ? 'default' : 'destructive'}>
                              {trace.effect}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!previewResult && !previewRunning && (
                <div className="rounded-lg border border-dashed p-12 text-center">
                  <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Configure and run a live preview</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Select a principal, connector, and query to see real retrieval results split by allowed and denied access, with full policy trace.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Batch Preview tab */}
          <TabsContent value="batch-preview">
            <div className="space-y-4 mt-4">
              <div className="rounded-lg border bg-card p-6">
                <h3 className="font-semibold mb-1">Batch Live Preview</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Run one search and evaluate policies for up to 5 principals simultaneously.
                </p>
                <form onSubmit={handleBatchPreview} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Principals <span className="text-muted-foreground">(select up to 5)</span>
                    </label>
                    {principalsLoading ? (
                      <Skeleton className="h-24 w-full" />
                    ) : (
                      <div className="max-h-36 overflow-y-auto rounded border p-2 space-y-1">
                        {(principals ?? []).map((p) => (
                          <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/50 px-2 py-1 rounded">
                            <input
                              type="checkbox"
                              checked={batchPrincipalIds.includes(p.id)}
                              disabled={!batchPrincipalIds.includes(p.id) && batchPrincipalIds.length >= 5}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBatchPrincipalIds((ids) => [...ids, p.id]);
                                } else {
                                  setBatchPrincipalIds((ids) => ids.filter((id) => id !== p.id));
                                }
                              }}
                            />
                            <span className="truncate">{p.display_name}</span>
                            <span className="text-xs text-muted-foreground truncate">{p.email}</span>
                          </label>
                        ))}
                        {(principals ?? []).length === 0 && (
                          <p className="text-xs text-muted-foreground px-2 py-1">No principals found. Sync an identity provider first.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Connector</label>
                      {connectorsLoading ? (
                        <Skeleton className="h-10 w-full" />
                      ) : (
                        <Select value={batchConnectorId} onValueChange={setBatchConnectorId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select connector..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(connectors ?? []).map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Search Mode</label>
                      <Select value={batchSearchMode} onValueChange={(v) => setBatchSearchMode(v as SearchMode)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SEARCH_MODES.map((m) => (
                            <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {batchSearchMode === 'hybrid' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Alpha <span className="text-muted-foreground">(0 = keyword, 1 = vector)</span></label>
                      <input
                        type="range"
                        min={0} max={1} step={0.05}
                        value={batchAlpha}
                        onChange={(e) => setBatchAlpha(parseFloat(e.target.value))}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground text-right">{batchAlpha.toFixed(2)}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Query</label>
                    <Input
                      value={batchQuery}
                      onChange={(e) => setBatchQuery(e.target.value)}
                      placeholder="e.g. What is our Q4 revenue forecast?"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={batchRunning || batchPrincipalIds.length === 0 || !batchConnectorId || !batchQuery.trim()}
                    className="gap-2"
                  >
                    <FlaskConical className="h-4 w-4" />
                    {batchRunning ? 'Running…' : 'Run Batch Preview'}
                  </Button>
                </form>
              </div>

              {batchError && (
                <div className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-4">
                  <p className="text-sm text-status-warning">{batchError}</p>
                </div>
              )}

              {batchResult && batchResult.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Results Matrix</h3>
                  {batchResult.map((pr, idx) => {
                    const cfg = outcomeConfig[pr.outcome as keyof typeof outcomeConfig] ?? outcomeConfig.denied;
                    const Icon = cfg.icon;
                    return (
                      <div key={idx} className={`rounded-lg border p-4 ${cfg.bgClassName}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`h-4 w-4 ${cfg.className}`} />
                          <span className="font-medium text-sm">{pr.principal_name}</span>
                          <Badge variant="outline" className={`text-xs ${cfg.className}`}>{cfg.label}</Badge>
                          <span className="ml-auto text-xs text-muted-foreground">
                            {pr.allowed_results.length} allowed / {pr.denied_results.length} denied
                          </span>
                        </div>
                        {pr.denied_results.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Denied: {pr.denied_results.map((r) => r.denial_reasons?.[0] ?? 'POLICY_DENY').join(', ')}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </EntitlementGate>
    </div>
  );
}
